import PointDimensions from '@components/common/SublimeMedia/PointDimensions/PointDimensions'
import {
  MediaWithCropsFragment,
  VideoFragment,
} from '@lib/umbraco/generated/types.generated'
import clsx from 'clsx'
import {
  DetailedHTMLProps,
  FC,
  HTMLAttributes,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import useDimensions from 'react-cool-dimensions'
import SVG from 'react-inlinesvg'
import { useInView } from 'react-intersection-observer'
import mergeRefs from 'react-merge-refs'
import Gif from './Gif'
import Image from './Image'
import s from './Media.module.scss'
import Video from './Video'
interface MediaProps
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  mediaWithCrops: MediaWithCropsFragment
  disableShopTheLook?: boolean
  priority?: boolean
  sizes?: string
  className?: string
  mediaClassName?: string
  scaleOnHover?: boolean
  innerClassName?: string
  clickToZoom?: boolean
  onZoom?: (zoomed: boolean) => void
}

const Media: FC<MediaProps> = ({
  mediaWithCrops,
  priority,
  sizes,
  className,
  mediaClassName,
  disableShopTheLook,
  scaleOnHover = false,
  innerClassName,
  clickToZoom = false,
  onZoom,
  children,
  ...props
}) => {
  const outerContainer = useRef<HTMLDivElement>(null)
  const [inViewRef, inView, entry] = useInView({
    triggerOnce: true,
    threshold: 0,
  })
  const {
    observe,
    width: imageWidth,
    height: imageHeight,
  } = useDimensions<HTMLDivElement | null>()

  // https://www.chromium.org/audio-video/
  const websupportedVideoFormats = useMemo(
    () => ['mp4', 'webm', 'ogg', 'hls', 'wav'],
    [],
  )

  const isEcommerceVideo = useMemo(
    () =>
      websupportedVideoFormats.some(
        (format) =>
          mediaWithCrops?.media?.__typename === 'Image' &&
          mediaWithCrops.media?.url?.toLowerCase().endsWith(format),
      ),
    [mediaWithCrops, websupportedVideoFormats],
  )

  const ecommerceVideo: VideoFragment | null = useMemo(() => {
    if (!isEcommerceVideo) return null

    const video: VideoFragment = {
      id: mediaWithCrops.url.replace('https://media.gubi.com/', ''),
      disableAutoplay: false,
      disableLoop: false,
      enableControls: false,
      enableSound: false,
      mobileVideo: null,
      thumbnailImage: null,
      url: mediaWithCrops.url,
      caption: null,
      videoSizing: null,
      thumbnailSrc: null,
    }

    return video
  }, [mediaWithCrops, isEcommerceVideo])

  const mediaRef = useRef<HTMLImageElement>(null)
  const mouseTimeout = useRef<NodeJS.Timeout | null>(null)

  const toggleZoom = (e) => {
    if (!mediaRef.current) return

    if (!mediaRef.current.classList.contains(s.zoomed)) {
      const rect = mediaRef.current.getBoundingClientRect()
      const offsetX = e.nativeEvent.offsetX
      const offsetY = e.nativeEvent.offsetY
      const originX = (offsetX / rect.width) * 100
      const originY = (offsetY / rect.height) * 100
      mediaRef.current.classList.add(s.zoomed)
      if (onZoom) onZoom(true)
      mediaRef.current.style.transformOrigin = `${originX}% ${originY}%`
    } else {
      mediaRef.current.classList.remove(s.zoomed)
      if (onZoom) onZoom(false)
      mediaRef.current.style.transformOrigin = `50% 50%`
    }
  }

  useEffect(() => {
    const handlePan = (e) => {
      if (!mediaRef.current || !mediaRef.current.classList.contains(s.zoomed))
        return

      e.preventDefault()

      const rect = mediaRef.current.getBoundingClientRect()

      const min = 0
      const max = 100

      const x = Math.max(
        min,
        Math.min(((e.clientX - rect.left) / rect.width) * 100, max),
      )
      const y = Math.max(
        min,
        Math.min(((e.clientY - rect.top) / rect.height) * 100, max),
      )

      mediaRef.current.style.transformOrigin = `${x}% ${y}%`
    }

    const handleMouseLeave = () => {
      if (mediaRef.current && mediaRef.current.classList.contains(s.zoomed)) {
        // after 3 seconds, remove the zoomed class
        mouseTimeout.current = setTimeout(() => {
          mediaRef.current?.classList.remove(s.zoomed)
          if (onZoom) onZoom(false)
          mediaRef.current.style.transformOrigin = `50% 50%`
        }, 1000)
      }
    }

    const handleMouseEnter = () => {
      if (mediaRef.current && mediaRef.current.classList.contains(s.zoomed)) {
        // clear the timeout
        clearTimeout(mouseTimeout.current)
        mouseTimeout.current = null
      }
    }

    if (!mediaRef.current) return

    mediaRef.current.addEventListener('mousemove', handlePan)
    mediaRef.current.addEventListener('mouseleave', handleMouseLeave)
    mediaRef.current.addEventListener('mouseenter', handleMouseEnter)

    return () => {
      mediaRef.current?.removeEventListener('mousemove', handlePan)
      mediaRef.current?.removeEventListener('mouseleave', handleMouseLeave)
      mediaRef.current?.removeEventListener('mouseenter', handleMouseEnter)

      if (mouseTimeout.current) {
        clearTimeout(mouseTimeout.current)
      }
    }
  }, [mediaRef.current])

  if (!mediaWithCrops) return null

  return (
    <div
      data-id={mediaWithCrops.media?.id}
      ref={mergeRefs([inViewRef, outerContainer, observe])}
      className={clsx(s.observer, className)}
      onClick={clickToZoom ? toggleZoom : undefined}
      {...props}
    >
      {children}

      {!disableShopTheLook &&
        mediaWithCrops?.media?.__typename == 'Image' &&
        mediaWithCrops.media.shopTheLookJson && (
          <PointDimensions
            outerContainer={outerContainer}
            mediaPicker={mediaWithCrops}
            width={imageWidth}
            height={imageHeight}
            loaded={true}
            inViewport={inView}
          />
        )}
      {(mediaWithCrops.media?.__typename === 'UmbracoMediaVideo' ||
        mediaWithCrops.url?.endsWith('.mp4') ||
        isEcommerceVideo) && (
        <Video
          video={
            isEcommerceVideo
              ? ecommerceVideo
              : (mediaWithCrops.media as VideoFragment)
          }
          inView={inView}
          priority={priority}
          className={clsx(mediaClassName, innerClassName, s.video)}
        ></Video>
      )}
      {mediaWithCrops.media?.__typename === 'Image' &&
        !mediaWithCrops.media.url?.endsWith('.gif') &&
        !isEcommerceVideo && (
          <Image
            ref={mediaRef}
            image={mediaWithCrops.media}
            inView={inView}
            className={clsx(mediaClassName, innerClassName, s.image, {
              [s.scaleOnHover]: scaleOnHover,
              [s.clickToZoom]: clickToZoom,
            })}
            sizes={sizes}
            priority={priority}
          />
        )}
      {mediaWithCrops.media?.__typename === 'UmbracoMediaVectorGraphics' && (
        // @ts-ignore
        <SVG
          className={clsx(innerClassName)}
          src={`https://media.gubi.com/${mediaWithCrops.media.id}.svg`}
          onError={(error) => console.log(error.message)}
        />
      )}
      {mediaWithCrops.media?.__typename === 'Image' &&
        mediaWithCrops.media.url?.endsWith('.gif') && (
          <Gif
            inView={inView}
            gif={mediaWithCrops.media}
            className={clsx(mediaClassName, innerClassName)}
            sizes={sizes}
            priority={priority}
          />
        )}
    </div>
  )
}

export default Media
